import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsService } from '../../services/supabaseService';


const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [activeDealCount, setActiveDealCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated') === 'true';
    const role = localStorage.getItem('userRole');
    const verified = localStorage.getItem('isVerified') === 'true';
    const dealCount = parseInt(localStorage.getItem('activeDealCount') || '0', 10);
    const savedLanguage = localStorage.getItem('language') || 'en';
    const userData = localStorage.getItem('user');
    const parsedUser = userData ? JSON.parse(userData) : null;

    setIsAuthenticated(authStatus);
    setUserRole(role);
    setIsVerified(verified);
    setActiveDealCount(dealCount);
    setCurrentLanguage(savedLanguage);
    setUser(parsedUser);

    // Load unread notification count
    if (parsedUser?.id) {
      loadUnreadNotificationCount(parsedUser?.id);
    }
  }, []);

  const loadUnreadNotificationCount = async (userId) => {
    try {
      const count = await notificationsService?.getUnreadCount(userId);
      setUnreadNotificationCount(count);
    } catch (error) {
      console.error('Load unread notification count error:', error);
    }
  };

  const translations = {
    en: {
      dashboard: 'Dashboard',
      deals: 'Deals',
      profile: 'Profile',
      admin: 'Admin',
      aiMonitoring: 'AI Monitoring',
      userManagement: 'User Management',
      notifications: 'Notifications',
      login: 'Login',
      logout: 'Logout',
      buyer: 'Buyer',
      traveler: 'Traveler',
      verified: 'Verified',
      unverified: 'Unverified'
    },
    ko: {
      dashboard: '대시보드',
      deals: '거래',
      profile: '프로필',
      admin: '관리자',
      aiMonitoring: 'AI 모니터링',
      userManagement: '사용자 관리',
      notifications: '알림',
      login: '로그인',
      logout: '로그아웃',
      buyer: '구매자',
      traveler: '여행자',
      verified: '인증됨',
      unverified: '미인증'
    }
  };

  const t = translations?.[currentLanguage];

  const navigationItems = [
    {
      label: t?.dashboard,
      path: '/unified-dashboard',
      icon: 'LayoutDashboard',
      roles: ['buyer', 'traveler', 'admin']
    },
    {
      label: currentLanguage === 'en' ? 'My Trips' : '내 여행',
      path: '/traveler-trip-creation',
      icon: 'Plane',
      roles: ['traveler', 'admin']
    },
    {
      label: t?.deals,
      path: '/unified-dashboard',
      icon: 'Handshake',
      roles: ['buyer', 'traveler', 'admin'],
      badge: activeDealCount > 0 ? activeDealCount : null
    },
    {
      label: t?.notifications,
      path: '/notifications-hub',
      icon: 'Bell',
      roles: ['buyer', 'traveler', 'admin'],
      badge: unreadNotificationCount > 0 ? unreadNotificationCount : null
    },
    {
      label: t?.profile,
      path: user?.id ? `/traveler-profile/${user?.id}` : '/unified-dashboard',
      icon: 'User',
      roles: ['buyer', 'traveler', 'admin']
    },
    {
      label: t?.aiMonitoring,
      path: '/ai-monitoring-dashboard',
      icon: 'Activity',
      roles: ['admin']
    },
    {
      label: t?.userManagement,
      path: '/admin-user-management',
      icon: 'Users',
      roles: ['admin']
    }
  ];

  const visibleNavItems = navigationItems?.filter(item => 
    !item?.roles || (userRole && item?.roles?.includes(userRole))
  );

  const isActive = (path) => location?.pathname === path;

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'ko' : 'en';
    setCurrentLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('isVerified');
    localStorage.removeItem('activeDealCount');
    setIsAuthenticated(false);
    setUserRole(null);
    navigate('/authentication-screen');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-20 bg-card shadow-elevation-md z-100 transition-base">
        <div className="h-full flex items-center justify-between px-6 pt-3">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-3 transition-base hover:opacity-80">
  <img
    src="/assets/images/ogada-logo-1770865214464.png"
    alt="OGADA Logo"
    className="h-10 w-auto object-contain"
  />
  <span className="text-xl font-heading font-semibold text-foreground">OGADA</span>
</Link>

            <nav className="hidden lg:flex items-center gap-2">
              {visibleNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`relative flex items-center gap-2 px-4 h-12 rounded-lg transition-base ${
                    isActive(item?.path)
                      ? 'text-foreground font-semibold'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="font-medium">{item?.label}</span>
                  {item?.badge && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-bold rounded-full flex items-center justify-center">
                      {item?.badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="hidden lg:flex items-center gap-2 px-3 h-10 rounded-lg bg-muted hover:bg-muted/80 transition-base"
              aria-label="Toggle language"
            >
              <Icon name="Languages" size={18} />
              <span className="text-sm font-medium">{currentLanguage?.toUpperCase()}</span>
            </button>

            {isAuthenticated ? (
              <div className="hidden lg:flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 h-10 rounded-lg bg-muted">
                  <Icon name="User" size={18} />
                  <span className="text-sm font-medium capitalize">{userRole}</span>
                  {isVerified && (
                    <Icon name="BadgeCheck" size={16} color="var(--color-success)" />
                  )}
                </div>
                {userProfile?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/admin-user-management')}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Icon name="Shield" size={16} />
                    Admin Panel
                  </button>
                )}
                <button
                  onClick={async () => {
                    await signOut();
                    localStorage.removeItem('isAuthenticated');
                    localStorage.removeItem('userRole');
                    localStorage.removeItem('isVerified');
                    localStorage.removeItem('activeDealCount');
                    setIsAuthenticated(false);
                    setUserRole(null);
                    navigate('/authentication-screen');
                  }}
                  className="flex items-center gap-2 px-4 h-10 rounded-lg bg-destructive text-destructive-foreground hover:opacity-90 transition-base"
                >
                  <Icon name="LogOut" size={18} />
                  <span className="text-sm font-medium">{t?.logout}</span>
                </button>
              </div>
            ) : (
              <Link
                to="/authentication-screen"
                className="hidden lg:flex items-center gap-2 px-4 h-10 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-base"
              >
                <Icon name="LogIn" size={18} />
                <span className="text-sm font-medium">{t?.login}</span>
              </Link>
            )}

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 transition-base"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-16 bg-background z-50 lg:hidden">
          <nav className="flex flex-col p-4 gap-2">
            {visibleNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`relative flex items-center gap-3 px-4 h-14 rounded-lg transition-base ${
                  isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={24} />
                <span className="font-medium text-lg">{item?.label}</span>
                {item?.badge && (
                  <span className="ml-auto w-6 h-6 bg-accent text-accent-foreground text-sm font-bold rounded-full flex items-center justify-center">
                    {item?.badge}
                  </span>
                )}
              </Link>
            ))}

            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-3 px-4 h-14 w-full rounded-lg bg-muted hover:bg-muted/80 transition-base"
              >
                <Icon name="Languages" size={24} />
                <span className="font-medium text-lg">{currentLanguage?.toUpperCase()}</span>
              </button>

              {isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 px-4 h-14 mt-2 rounded-lg bg-muted">
                    <Icon name="User" size={24} />
                    <span className="font-medium text-lg capitalize">{userRole}</span>
                    {isVerified && (
                      <Icon name="BadgeCheck" size={20} color="var(--color-success)" />
                    )}
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 h-14 w-full mt-2 rounded-lg bg-destructive text-destructive-foreground hover:opacity-90 transition-base"
                  >
                    <Icon name="LogOut" size={24} />
                    <span className="font-medium text-lg">{t?.logout}</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/authentication-screen"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 h-14 w-full mt-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-base"
                >
                  <Icon name="LogIn" size={24} />
                  <span className="font-medium text-lg">{t?.login}</span>
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;